import React, { useEffect, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { NumericFormat } from 'react-number-format';
import round from 'lodash/round';
import classNames from '../../../utility/classNames';
import DescriptionTooltipModal from './DescriptionTooltipModal';
import Tooltip from '../../Tooltip';
import {
  formatLargeNumber,
  getNumberWithCommas,
} from '../../../utility/formatNumbers';

function UnitMoneyInput({
  label,
  layout = 'default',
  type = 'text',
  showAltValue = false,
  derivedFrom,
  description,
  tooltipHeading,
  tooltipFormula,
  externalData,
  required,
  selectOptions,
  preValue,
  ...props
}) {
  const [field, meta, helpers] = useField(props);
  const { value, touched } = meta;
  const { setValue, setTouched } = helpers;
  const { values: formValues } = useFormikContext();
  const [unitOption, setUnitOption] = useState(value?.unit || selectOptions[0]);

  const [error, setError] = useState(undefined);
  const getUnitName = (select) => {
    switch (select) {
      case '%':
        return '%';
      case 'amount':
      case 'monthly':
      case 'yearly':
      default:
        return '$';
    }
  };

  const unit = getUnitName(unitOption);

  const total =
    formValues?.[derivedFrom] || externalData?.[derivedFrom] || undefined;

  useEffect(() => {
    if (preValue) {
      setValue(preValue);
    }
  }, [preValue]);

  useEffect(() => {
    if (!required) return;

    if (touched) {
      if (!value) {
        setError('This field is required');
      } else {
        setError(undefined);
      }
    }
  }, [touched, value]);

  useEffect(() => {
    if (!value?.unit) {
      setUnitOption(selectOptions[0]);
      setValue({ unit: selectOptions[0], value: null, derivedFrom });
      return;
    }

    if (value.unit !== unitOption) {
      setUnitOption(value.unit);
    }
  }, [value]);

  const handleUnitSelect = (select) => {
    setTouched(true);
    setUnitOption(select);
    if (value) {
      if (select === '%') {
        const newPercentage = round((value.value / total) * 100, 2);
        setValue({ unit: select, value: newPercentage, derivedFrom });
      } else if (select === 'amount') {
        const newValue = round(value.value * (total / 100), 2);

        setValue({ unit: select, value: newValue, derivedFrom });
      } else if (select === 'monthly' || select === 'yearly') {
        setValue({
          unit: select,
          value:
            value.unit === '%'
              ? round(value.value * (total / 100), 2)
              : value.value,
          derivedFrom,
        });
      }
    } else {
      setValue({ unit: select, value: null, derivedFrom });
    }
  };

  let altValue = null;
  if (showAltValue && total && value?.unit) {
    switch (value.unit) {
      case '%':
        const val = round(value.value * (total / 100), 2);
        const valString = formatLargeNumber(val);
        altValue = valString ? `$${valString}` : null;
        break;
      case 'amount':
      case 'monthly':
        const val2 = round((value.value / total) * 100, 2);
        const valString2 = getNumberWithCommas(val2);
        altValue = valString2 ? `${valString2}%` : null;
        break;
      case 'yearly':
        const val3 = round((value.value / 12 / total) * 100, 2);
        const valString3 = getNumberWithCommas(val3);
        altValue = valString3 ? `${valString3}%` : null;
        break;
    }
  }

  //sourceInfo is an argument required by react-number-format to indicate if the change
  //is coming from user input or a change in props
  //https://s-yadav.github.io/react-number-format/docs/quirks#sourceInfo

  const handleValueChange = (values, sourceInfo) => {
    const { floatValue } = values;
    const unit = unitOption;
    // if user empyty the input, set value to null
    if (!floatValue && floatValue !== 0) {
      setValue(null);
      return;
    }
    setValue({ unit: unit, value: floatValue, derivedFrom });
    setTouched(true);
  };

  return (
    <div
      className={classNames(
        (layout === 'compact' || layout === 'tableNoLabel') && 'my-0 space-y-0',
        layout === 'tableNoLabel' && 'w-full'
      )}
    >
      <div
        className={classNames(
          'sm:grid sm:items-start sm:gap-2',
          layout === 'stacked' && 'sm:gap-2',
          (layout === 'compact' || layout === 'tableNoLabel') &&
            'sm:gap-4 pt-0 border-none',
          layout === 'tableNoLabel' ? 'sm:grid-cols-2' : 'sm:grid-cols-3-inputs'
        )}
      >
        {layout !== 'tableNoLabel' && (
          <label
            htmlFor={field.name}
            className={classNames(
              'h-full flex items-center',

              layout === 'stacked' && 'col-span-3'
            )}
          >
            <span className="text-md font-normal text-neutral-900">
              {label}
              {required && <span title="required">*</span>}{' '}
              {description && layout === 'compact' && (
                <DescriptionTooltipModal description={description} />
              )}
              {description && layout !== 'compact' && (
                <Tooltip
                  text={description}
                  heading={tooltipHeading}
                  formula={tooltipFormula}
                  classes="whitespace-normal"
                  type="info"
                />
              )}
            </span>
          </label>
        )}

        <div
          className={classNames(
            layout === 'stacked' ? 'col-span-3' : 'col-span-2',
            'rounded'
          )}
        >
          <div className="flex w-full">
            <div className={classNames('block w-full relative mr-2')}>
              <NumericFormat
                id={field.name}
                value={value?.value || ''}
                prefix={unit === '$' ? unit : undefined}
                suffix={unit === '%' ? unit : undefined}
                onValueChange={handleValueChange}
                displayType="input"
                thousandSeparator={true}
                decimalScale={2}
                allowNegative={false}
                onBlur={() => setTouched(true)}
                type={type}
                {...props}
                className={classNames(
                  'block w-full border focus:border-primary bg-neutral-input focus:outline-none focus:ring-transparent  text-md  h-8 rounded-md py-0 px-3 placeholder-neutral-500 placeholder-opacity-90',
                  touched && error ? 'border-warning' : 'border-transparent'
                )}
              />
              {unit && unit !== '%' && unit !== '$' && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span
                    className="text-neutral-900 text-md"
                    id="price-currency"
                  >
                    {unit}
                  </span>
                </div>
              )}
              {altValue && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span
                    className="text-neutral-900 text-md tracking-wide"
                    id="price-currency"
                  >
                    ({altValue})
                  </span>
                </div>
              )}
            </div>

            <select
              value={unitOption}
              onChange={(e) => {
                e.preventDefault();
                handleUnitSelect(e.target.value);
              }}
              className="block  w-[128px] pl-4 pr-7 border-transparent border bg-neutral-input focus:outline-none focus:ring-transparent focus:border-neutral-900  text-md  h-8 rounded-md py-0 px-3 placeholder-neutral-500 placeholder-opacity-90"
            >
              {selectOptions?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {touched && error ? (
            <div className="error text-base font-small text-warning ">
              {error}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default UnitMoneyInput;
